import { useParams, useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ContentBlockRenderer from "@/components/content/ContentBlockRenderer";
import { useContentItem } from "@/hooks/useContentItem";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { staggerContainer, heroVariant, sectionVariant, viewportOnce, itemVariant } from "@/lib/motion";
import NotFound from "@/pages/NotFound";

const CaseStudyPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const previewMode = searchParams.get("preview") === "true" && !!user;

  const { data: item, isLoading } = useContentItem(slug, previewMode);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-36 pb-24 container-wide max-w-4xl">
          <Skeleton className="h-6 w-32 mb-4" />
          <Skeleton className="h-12 w-full mb-6" />
          <Skeleton className="h-6 w-2/3 mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!item || item.status === "archived") return <NotFound />;
  if ((item.status === "draft" || item.status === "review") && !previewMode) return <NotFound />;

  const content = item.content || {};
  const seo = item.seo || {};
  const blocks = content.blocks || [];
  const metrics = content.metrics || [];
  const services = content.servicesUsed || [];
  const contextualImage = content.hero_image || null; // contextual image for case studies
  const ogImage = seo.ogImage || content.og_image?.src || null;

  const caseStudySchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: seo.title || item.title,
    description: seo.description || item.excerpt || "",
    datePublished: item.published_at,
    dateModified: item.updated_at,
    ...(ogImage && { image: ogImage }),
    author: { "@type": "Organization", name: "XperfoMax" },
    publisher: { "@type": "Organization", name: "XperfoMax", url: "https://xperfomax.com" },
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seo.title || item.title}
        description={seo.description || item.excerpt || ""}
        canonicalUrl={seo.canonicalUrl || `/case-studies/${item.slug}`}
        ogImage={ogImage}
        schema={caseStudySchema}
      />
      <Header />

      {previewMode && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/30 text-center py-2">
          <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
            Preview Mode. This content is not published.
          </span>
        </div>
      )}

      {/* Hero */}
      <section className="pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20 border-b border-border">
        <div className="container-wide">
          <div className="max-w-4xl">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={heroVariant}>
                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Case Studies
                </Link>
              </motion.div>

              {content.industry && (
                <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4 block">
                  {content.industry}
                </motion.span>
              )}

              <motion.h1 variants={heroVariant} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-6 leading-[1.15]">
                {item.title}
              </motion.h1>

              {item.excerpt && (
                <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                  {item.excerpt}
                </motion.p>
              )}

              {content.period && (
                <motion.div variants={heroVariant} className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-sm text-muted-foreground">
                  <span>Engagement: {content.period}</span>
                  {content.client && <span>Client: {content.client}</span>}
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      {metrics.length > 0 && (
        <section className="py-12 border-b border-border bg-muted/20">
          <div className="container-wide">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl"
              initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}
            >
              {metrics.map((metric: { label: string; value: string }, i: number) => (
                <motion.div key={i} variants={itemVariant} className="text-center">
                  <span className="text-2xl md:text-3xl font-semibold text-foreground block">{metric.value}</span>
                  <span className="text-sm text-muted-foreground mt-1 block">{metric.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 md:py-20 lg:py-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-8">
              <motion.article initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
                <ContentBlockRenderer blocks={blocks} />
              </motion.article>

              {/* Contextual image after content */}
              {contextualImage?.src && (
                <figure className="mt-12 border border-border">
                  <img
                    src={contextualImage.src}
                    alt={contextualImage.alt || item.title}
                    width={contextualImage.width || 1200}
                    height={contextualImage.height || 630}
                    loading="lazy"
                    className="w-full h-auto max-h-[400px] object-cover"
                  />
                </figure>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <motion.div
                initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}
                className="sticky top-32 space-y-6"
              >
                {services.length > 0 && (
                  <motion.div variants={itemVariant} className="p-6 border border-border">
                    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-4">
                      Services Used
                    </h3>
                    <ul className="space-y-3">
                      {services.map((service: { title: string; href: string }, i: number) => (
                        <li key={i}>
                          <Link
                            to={service.href}
                            className="text-foreground hover:text-primary transition-colors flex items-center justify-between group text-sm"
                          >
                            {service.title}
                            <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                <motion.div variants={itemVariant} className="p-6 border border-border bg-muted/30">
                  <h3 className="text-lg font-medium text-foreground mb-3">Want similar results?</h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    Let's discuss how we can apply these strategies to your business.
                  </p>
                  <Button className="w-full group" asChild>
                    <Link to="/contact">
                      Discuss your goals
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </motion.div>
              </motion.div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyPost;
